import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { cn } from "../../lib/utils";

const NAV = [
  { label: "Expertise", href: "#expertise", id: "expertise" },
  { label: "Approach", href: "#approach", id: "approach" },
  { label: "About", href: "#about", id: "about" },
  { label: "Vision", href: "#vision", id: "vision" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 16);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = NAV.map((n) => document.getElementById(n.id)).filter(
      (el): el is HTMLElement => Boolean(el),
    );
    if (!sections.length || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: [0, 0.2, 0.6] },
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500",
        scrolled || open
          ? "border-b border-hairline bg-background/75 backdrop-blur-xl"
          : "border-b border-transparent",
      )}
    >
      <div
        className={cn(
          "mx-auto grid max-w-[1400px] grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-6 transition-[padding] duration-500 md:px-10",
          scrolled ? "py-3 lg:py-3.5" : "py-5 lg:py-7",
        )}
      >
        <a
          href="/"
          aria-label="Billionaire Mindset Technologies — home"
          className="flex min-w-0 items-center"
        >
          <Logo
            priority
            className={cn(
              "shrink-0 transition-[width] duration-500",
              scrolled ? "w-[132px] sm:w-[148px]" : "w-[152px] sm:w-[184px]",
            )}
          />
        </a>

        <nav aria-label="Primary" className="hidden items-center gap-1 md:flex">
          {NAV.map((item, i) => {
            const isActive = active === item.id;
            return (
              <a
                key={item.href}
                href={item.href}
                aria-current={isActive ? "true" : undefined}
                className={cn(
                  "group relative flex items-baseline gap-2 px-4 py-2 text-[0.78rem] tracked transition-colors duration-300",
                  isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground",
                )}
              >
                <span className="num text-[0.6rem] text-primary/70 transition-opacity duration-300 group-hover:opacity-100">
                  0{i + 1}
                </span>
                {item.label}
                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute inset-x-4 bottom-0 h-px origin-left bg-primary transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                    isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100",
                  )}
                />
              </a>
            );
          })}
        </nav>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="mobile-nav"
          aria-label={open ? "Close menu" : "Open menu"}
          className="flex size-10 shrink-0 items-center justify-center border border-hairline text-foreground transition-colors duration-300 hover:border-primary/60 md:hidden"
        >
          {open ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </div>

      <div
        aria-hidden="true"
        className="h-px origin-left bg-primary/70 transition-transform duration-150 ease-out"
        style={{ transform: `scaleX(${progress})` }}
      />

      <div
        id="mobile-nav"
        hidden={!open}
        className="border-t border-hairline bg-background/95 backdrop-blur-xl md:hidden"
      >
        <nav aria-label="Mobile" className="flex flex-col px-6 py-2">
          {NAV.map((item, i) => (
            <a
              key={item.href}
              href={item.href}
              onClick={() => setOpen(false)}
              className="flex items-baseline gap-4 border-b border-hairline py-4 font-display text-lg text-foreground last:border-0"
            >
              <span className="num text-[0.65rem] text-primary">0{i + 1}</span>
              {item.label}
            </a>
          ))}
        </nav>
      </div>
    </header>
  );
}
